import { IItemOrcamento } from "app/types/IItemOrcamento"
import globalMU from "globalMU"
import React, { useMemo } from "react"

export type DetalheItemProps = {
    item: IItemOrcamento
    tipoOrcamento: string
    mostrarLocalRetirada: boolean
    usarVendaComEntergaPosterior: boolean
    tratarVendaSobEncomenda: boolean
    utilizaRecursoEncomendaExpressa: boolean
    exibirQuantidadeEstoqueConsultaProduto: boolean
    ambienteOrcamento: boolean
    exibirPrecoComissaoConsultasProdutos: boolean
    exibirQuantidadeMinimaVendaConsultaProdutos: boolean
    apurarSTOrcamento: boolean
    mascaraQuantidade: number
    mascaraPrecoVenda: number
}

/**
 * `id` é obrigatório e é o que dá a key estável de cada linha — não dá para usar o label, porque
 * "Faturamento" aparece DUAS vezes (tipo de faturamento do item e faturamento do local de
 * retirada). Antes a key era `${label}-${index}`, que só não colidia por causa do index; qualquer
 * linha condicional aparecendo/desaparecendo remontava as linhas seguintes à toa.
 */
type LinhaDetalheItem = {
    id: string
    label: string
    value: React.ReactNode
    valueClass?: string
    valueStyle?: React.CSSProperties
}

type DetailRowProps = LinhaDetalheItem & {
    labelClass?: string
}

const DetailRowComponent = ({ id, label, value, labelClass = "", valueClass = "", valueStyle }: DetailRowProps) => (
    <>
        <label
            id={`DetalheItem_${id}_Label`}
            className={`_w-[200px] _bg-[#e4e6e8] _text-right _font-normal _mb-0 _p-2 ${labelClass}`}
        >
            {label}
        </label>
        <div id={`DetalheItem_${id}`} className={`_ml-1 ${valueClass}`} style={valueStyle}>
            {value}
        </div>
    </>
)

const DetailRow = React.memo(DetailRowComponent)
DetailRow.displayName = "DetalheItem_DetailRow"

const DetalheItemComponent = (props: DetalheItemProps) => {
    const {
        item,
        tipoOrcamento,
        mostrarLocalRetirada,
        usarVendaComEntergaPosterior,
        utilizaRecursoEncomendaExpressa,
        exibirQuantidadeEstoqueConsultaProduto,
        ambienteOrcamento,
        exibirQuantidadeMinimaVendaConsultaProdutos,
        apurarSTOrcamento,
        mascaraQuantidade,
        mascaraPrecoVenda
    } = props

    // Uma única useMemo monta todas as linhas: as formatações antes ficavam em 5 useMemo separadas
    // que eram TODAS dependência desta, então nenhuma delas evitava recálculo — só somava
    // comparação de dependência. Formatar aqui dentro dá o mesmo resultado com menos trabalho.
    const linhas = useMemo<LinhaDetalheItem[]>(() => {
        const linhas: LinhaDetalheItem[] = [
            {
                id: "Descricao",
                label: "Descrição",
                value: item?.DescricaoProduto,
                valueClass: "_bg-[#ffffff] _font-bold _pl-[10px] _pb-[5px]"
            },
            { id: "Referencia", label: "Referência", value: item?.ReferenciaProduto },
            { id: "Codigo", label: "Código", value: item?.Codigo },
            { id: "Unidade", label: "Unidade", value: item?.UnidadeProduto },
            { id: "Modelo", label: "Modelo", value: item?.ModeloProduto },
            {
                id: "PrecoUnitario",
                label: "Preço unitário",
                value: globalMU.formatarValorNumerico(item?.PrecoProduto, mascaraPrecoVenda, true)
            },
            {
                id: "DataPrevisaoEntrega",
                label: "Data de Previsão Entrega",
                value: globalMU.formatDate(item?.DataPrevisaoEntrega)
            },
            {
                id: "DataMarcadaEntrega",
                label: "Data de Marcada Entrega",
                value: globalMU.formatDate(item?.DataMarcadaEntrega)
            },
            { id: "CodigoFabricante", label: "Código do Fabricante", value: item?.CodigoFabricante },
            { id: "Fornecedor", label: "Fornecedor", value: item?.Fornecedor },
            { id: "SegundaUnidade", label: "2ª Unidade", value: item?.UnidadeProduto2 },
            { id: "Status", label: "Status", value: item?.StatusProduto },
            {
                id: "Classe",
                label: "Classe",
                value: `${item?.CodigoClasse} - ${item?.ClasseProduto}`
            },
            { id: "Disponibilidade", label: "Disponibilidade", value: item?.DescricaoDisponibilidade },
            {
                id: "FatorConversao",
                label: "Fator de Conversão",
                value: globalMU.formatNumber(item?.FatorConversao)
            },
            {
                id: "PrecoAVista",
                label: "Preço a Vista",
                value: globalMU.formatMoney(item?.PrecoAVista)
            },
            {
                id: "AliquotaIPI",
                label: "Alíquota IPI",
                value: globalMU.formatNumber(item?.AliquotaIPI)
            },
            {
                id: "ValorIPI",
                label: "Valor IPI",
                value: globalMU.formatNumber(item?.ValorIPI)
            },
            {
                id: "TextoTecnico",
                label: "Texto Técnico",
                value: item?.TextoTecnico,
                valueStyle: { whiteSpace: "pre-wrap" }
            }
        ]

        if (tipoOrcamento !== "Transferências") {
            linhas.push(
                { id: "TipoEntrega", label: "Tipo de Entrega", value: item?.DescricaoTipoEntrega },
                { id: "TurnoEntrega", label: "Turno Entrega", value: item?.DescricaoTurnoEntrega },
                {
                    id: "VendaSobEncomenda",
                    label: "Venda Sob Encomenda",
                    value: item?.VendaSobEncomenda ? "Sim" : "Não"
                },
                { id: "TipoFaturamento", label: "Faturamento", value: item?.DescricaoTipoFaturamento }
            )

            if (usarVendaComEntergaPosterior) {
                linhas.push({
                    id: "EntregaPosterior",
                    label: "Entrega Posterior",
                    value: item?.EntregaPosterior ? "Sim" : "Não"
                })
            }

            if (tipoOrcamento !== "VENDAS" && utilizaRecursoEncomendaExpressa) {
                linhas.push({
                    id: "EncomendaExpressa",
                    label: "Encomenda Expressa",
                    value: item?.EncomendaExpressa ? "Sim" : "Não"
                })
            }

            if (ambienteOrcamento) {
                linhas.push({ id: "Ambiente", label: "Ambiente", value: item?.Ambiente })
            }

            linhas.push({ id: "CentroCusto", label: "Centro de Custo", value: item?.NomeCentroCusto })
        }

        if (mostrarLocalRetirada) {
            linhas.push(
                { id: "FilialRetirada", label: "Filial de Retirada", value: item?.FilialRetirada },
                { id: "FilialTransferencia", label: "Filial de Transferência", value: item?.FilialTransferencia },
                { id: "FaturamentoLocalRetirada", label: "Faturamento", value: item?.TipoFaturamento }
            )
        }

        if (exibirQuantidadeEstoqueConsultaProduto) {
            linhas.push(
                {
                    id: "QtdDisponivel",
                    label: "Qtd Disponível",
                    value: globalMU.formatarValorNumerico(item?.QuantidadeEstoque, mascaraQuantidade, false)
                },
                {
                    id: "ComissaoVenda",
                    label: "Comissão Venda",
                    value: globalMU.formatNumber(item?.ComissaoVenda)
                }
            )
        }

        if (exibirQuantidadeMinimaVendaConsultaProdutos) {
            linhas.push({
                id: "QtdeMinimaVenda",
                label: "Qtde Mínima de Venda",
                value: globalMU.formatarValorNumerico(item?.MenorFracaoVenda, mascaraQuantidade, false)
            })
        }

        if (apurarSTOrcamento) {
            linhas.push({
                id: "ValorSubstituicao",
                label: "Valor Substituição",
                value: globalMU.formatNumber(item?.ValorSubstituicao)
            })
        }

        return linhas
    }, [
        item,
        tipoOrcamento,
        mostrarLocalRetirada,
        usarVendaComEntergaPosterior,
        utilizaRecursoEncomendaExpressa,
        exibirQuantidadeEstoqueConsultaProduto,
        ambienteOrcamento,
        exibirQuantidadeMinimaVendaConsultaProdutos,
        apurarSTOrcamento,
        mascaraQuantidade,
        mascaraPrecoVenda
    ])

    return (
        <div id="DetalheItem_Div" className="_grid _grid-cols-[min-content_auto] _items-center">
            {linhas.map((linha) => (
                <DetailRow
                    key={linha.id}
                    id={linha.id}
                    label={linha.label}
                    value={linha.value}
                    valueClass={linha.valueClass}
                    valueStyle={linha.valueStyle}
                />
            ))}
            <section id="DetalheItem_EspacamentoFinal" style={{ marginBottom: "100px" }} />
        </div>
    )
}

export const DetalheItem = React.memo(DetalheItemComponent)
DetalheItem.displayName = "DetalheItem"
